/**
 * ShareJobButton — Iter-290b
 *
 * Reusable "Share Job" button that opens a small menu with platform-specific
 * share options. Designed to drop onto any job/shift/event card.
 *
 * Props:
 *   job: {
 *     id: string                  // shift_id (required) — used to build share URL
 *     title: string               // job/event title
 *     company?: string            // client/company name
 *     location?: string           // "Atlanta, GA" or "Venue, Atlanta, GA"
 *     dateRange?: string          // "Jul 12 – Jul 14, 2026"
 *     payRate?: string|number     // "$25/hr" or just a number
 *     description?: string        // short description
 *     applyUrl?: string           // explicit full URL; if missing, derived from
 *                                 //  window.location.origin + `/jobs/${id}`
 *     roleName?: string           // optional, e.g. "Brand Ambassador"
 *   }
 *   testIdSuffix?: string         // optional unique fragment for data-testid
 *   variant?: 'icon'|'button'     // default 'button'
 *
 * Layered behaviour:
 *   • Tap the button → dropdown with: Facebook, LinkedIn, X, Email, SMS, Copy Link
 *   • Pre-composed share text includes title/company/location/dates/pay/desc + link.
 *   • All outbound shares use rel="noopener noreferrer" target="_blank".
 *   • Copy uses navigator.clipboard.writeText() with sonner toast fallback.
 *   • SMS uses `sms:?&body=` which most mobile OSes route correctly.
 *
 * Accessibility:
 *   • Each share option has its own data-testid (`share-{platform}-${id}`).
 *   • Menu opens on click; closes on outside click / Escape / option selection.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Share2, Facebook, Linkedin, Mail, MessageSquare, Link as LinkIcon, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';

function formatPayRate(payRate) {
  if (payRate === undefined || payRate === null || payRate === '') return null;
  if (typeof payRate === 'number') return `$${payRate}/hr`;
  const str = String(payRate);
  return str.startsWith('$') ? str : `$${str}/hr`;
}

// Iter-290c (#1) — best-effort share-click logger. Backend table is anonymous-safe;
// errors are swallowed so the share UX never breaks.
function logShareClick(jobId, platform) {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/jobs/${jobId}/share-click`;
    const body = JSON.stringify({ platform });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    }
  } catch (_e) { /* ignore */ }
}

function composeShareText(job, link) {
  const lines = [];
  if (job.title) lines.push(`📣 ${job.title}`);
  if (job.company) lines.push(`Company: ${job.company}`);
  if (job.location) lines.push(`Location: ${job.location}`);
  // Iter-290c (#2) — multi-day events should advertise the full range.
  // Caller can pass either `dateRange` (single string like "Jul 1–5, 2026")
  // or `dates` (free-text fallback). We surface whichever is non-empty.
  const dateLine = job.dateRange || (Array.isArray(job.dates) ? job.dates.join(', ') : job.dates);
  if (dateLine) lines.push(`Dates: ${dateLine}`);
  const pay = formatPayRate(job.payRate);
  if (pay) lines.push(`Pay: ${pay}`);
  if (job.roleName) lines.push(`Role: ${job.roleName}`);
  if (job.description) {
    const desc = job.description.length > 240 ? job.description.slice(0, 237) + '...' : job.description;
    lines.push('');
    lines.push(desc);
  }
  lines.push('');
  lines.push(`Apply: ${link}`);
  return lines.join('\n');
}

function buildShareLinks(job) {
  const fallbackApply = typeof window !== 'undefined'
    ? `${window.location.origin}/jobs/${job.id}`
    : `/jobs/${job.id}`;
  const link = job.applyUrl || fallbackApply;
  const text = composeShareText(job, link);
  const subject = job.title ? `Job Opportunity: ${job.title}` : 'Job Opportunity';
  const encodedLink = encodeURIComponent(link);
  const encodedText = encodeURIComponent(text);
  const encodedSubject = encodeURIComponent(subject);
  return {
    link,
    text,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}&quote=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedLink}`,
    x: `https://twitter.com/intent/tweet?text=${encodedText}`,
    email: `mailto:?subject=${encodedSubject}&body=${encodedText}`,
    sms: `sms:?&body=${encodedText}`,
  };
}

export default function ShareJobButton({ job, testIdSuffix, variant = 'button', className = '' }) {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const suffix = testIdSuffix || (job && job.id) || 'job';

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!job || !job.id) return null;
  const links = buildShareLinks(job);

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Iter-314 — Collision detection. If the button is closer to the
    // bottom of the viewport than the menu height (~340 px for all 6
    // share options), flip the menu open UPWARD so it stays visible.
    // Previously the menu opened downward and disappeared off-screen
    // for cards near the bottom of the list — users reported the
    // Share button "not working."
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const MENU_HEIGHT = 340;
      setOpenUpward(spaceBelow < MENU_HEIGHT && rect.top > MENU_HEIGHT);
    }
    setOpen((v) => !v);
  };

  const handleShare = (e, url, platform) => {
    e.preventDefault();
    e.stopPropagation();
    logShareClick(job.id, platform.toLowerCase());
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
    toast.success(`Opened ${platform} share`, { duration: 1800 });
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    logShareClick(job.id, 'copy');
    try {
      await navigator.clipboard.writeText(links.link);
      toast.success('Job link copied to clipboard');
    } catch {
      // Fallback for older browsers — create a hidden textarea
      const ta = document.createElement('textarea');
      ta.value = links.link;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success('Job link copied'); }
      catch { toast.error('Copy failed — copy the URL manually'); }
      document.body.removeChild(ta);
    }
    setOpen(false);
  };

  const buttonBase = variant === 'action'
    ? 'w-full flex flex-col items-center justify-center gap-1 py-2 rounded-lg text-[10px] font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 transition'
    : variant === 'icon'
      ? 'p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-cyan-700 transition'
      : 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-cyan-50 hover:border-cyan-300 hover:text-cyan-700 transition';

  return (
    <div className={`relative inline-block ${variant === 'action' ? 'w-full' : ''} ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className={buttonBase}
        data-testid={`share-job-btn-${suffix}`}
        aria-label="Share job"
        aria-expanded={open}
      >
        <Share2 className={variant === 'icon' || variant === 'action' ? 'w-3.5 h-3.5' : 'w-3.5 h-3.5'} />
        {variant === 'action' && <span className="leading-none">Share</span>}
        {variant !== 'icon' && variant !== 'action' && <span>Share</span>}
      </button>

      {open && (
        <div
          ref={menuRef}
          className={`absolute right-0 z-50 w-56 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden ${openUpward ? 'bottom-full mb-1.5' : 'mt-1.5'}`}
          role="menu"
          data-testid={`share-job-menu-${suffix}`}
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-400 border-b border-slate-100">Share job</div>
          <button onClick={(e) => handleShare(e, links.facebook, 'Facebook')} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid={`share-facebook-${suffix}`}>
            <Facebook className="w-4 h-4 text-blue-600" /> Facebook
          </button>
          <button onClick={(e) => handleShare(e, links.linkedin, 'LinkedIn')} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid={`share-linkedin-${suffix}`}>
            <Linkedin className="w-4 h-4 text-blue-700" /> LinkedIn
          </button>
          <button onClick={(e) => handleShare(e, links.x, 'X')} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid={`share-x-${suffix}`}>
            <XIcon className="w-4 h-4 text-slate-900" /> X
          </button>
          <button onClick={(e) => handleShare(e, links.email, 'Email')} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid={`share-email-${suffix}`}>
            <Mail className="w-4 h-4 text-slate-600" /> Email
          </button>
          <button onClick={(e) => handleShare(e, links.sms, 'SMS')} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid={`share-sms-${suffix}`}>
            <MessageSquare className="w-4 h-4 text-emerald-600" /> SMS / Text
          </button>
          <button onClick={handleCopy} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100" data-testid={`share-copy-${suffix}`}>
            <LinkIcon className="w-4 h-4 text-cyan-600" /> Copy link
          </button>
        </div>
      )}
    </div>
  );
}
